import { uploadFile } from "@/common/utils";
import { UploadStates, type FileOptions, type FileToUpload } from "./useUploadManager";
import type { Ref } from "vue";

export const getFileSizeLabel = (fileSize: number) => {
  if (fileSize < 1000) return `${fileSize} B`;
  if (fileSize < 1000000) return `${(fileSize / 1000).toFixed(0)} kB`;
  if (fileSize < 1000000000) return `${(fileSize / 1000000).toFixed(0)} MB`;
  return `${(fileSize / 1000000000).toFixed(2)} GB`;
};


/**
 * Adapted from
 * https://github.com/react-dropzone/attr-accept/blob/main/src/index.js
 */

export const validFileType = (file: File, acceptedFiles: string[]) => {
  console.log('validFileType', file, acceptedFiles.length)
  if (acceptedFiles.length === 0) return true
  const fileName = (file.name || '').toLowerCase()
  const mimeType = (file.type || '').toLowerCase()
  const baseMimeType = mimeType.replace(/\/.*$/, '')
  console.log('baseMimeType', baseMimeType, fileName, mimeType)
  return acceptedFiles.some(type => {
    console.log('type', type)
    const validType = type.trim().toLowerCase()
    if (validType.charAt(0) === '.') {
      return fileName.endsWith(validType)
    } else if (validType.endsWith('/*')) {
      // This is something like a image/* mime type
      return baseMimeType === validType.replace(/\/.*$/, '')
    }
    return mimeType === validType
  })
}

export const validFileSize = (file: File, maxFileSize: number) => {
  return file.size <= maxFileSize;
}

export const VALIDATIONERRORS = {
  FILESIZE: 'FILESIZE',
  FILETYPE: 'FILETYPE'
} as const;

type ValidationError = typeof VALIDATIONERRORS[keyof typeof VALIDATIONERRORS];

export const validateFile = (file: File, options: FileOptions) => {
  const errors: ValidationError[] = [];
  if (!validFileSize(file, options.maxFilesize)) errors.push(VALIDATIONERRORS.FILESIZE);
  if (!validFileType(file, options.acceptedFiles)) errors.push(VALIDATIONERRORS.FILETYPE);
  return errors;
}

export const isValid = (file: Ref<FileToUpload>, options: FileOptions) => {
  const result = validateFile(file.value.file, options);
  return result.length === 0;
}

export const getErrors = (file: Ref<FileToUpload>, options: FileOptions) => {
  return validateFile(file.value.file, options);
}

// for development purposes we can mock the upload function
const mockUploadfunction = async (url: string, file: File, header: Record<string, string>[], cb: (progress: number) => void):
  Promise<{ status: number; body: string }> => {
  console.log('mock upload');
  // simulate upload progress with random duration
  // 0 - 5 sec
  const duration = Math.round(Math.random() * 1000 * 5);
  const cbSteps = Math.round(duration / 5);
  let elapsed = 0;
  const cbIntervall = setInterval(() => {
    elapsed += cbSteps;
    cb(Math.round(elapsed / duration * 100));
    // 5 cb steps
  }, cbSteps);
  return new Promise((resolve) => {
    setTimeout(() => {
      clearInterval(cbIntervall);
      cb(100);
      const success = Math.random() > 0.5;
      resolve({
        status: success ? 200 : 500,
        body: success ? '{"status": "ok"}' : '{"status": "failed"}',
      });
    }, duration);
  });
};

// we pass the token to the upload function instead of passing it at creation time
// so we have the possibility to retry the upload with a new token if we need to
// this is a bit more flexible and future proof if we decide to change it to a
// more transactional model for the token generation
export const uploadFileToEndpoint = async (urlapiEndpoint: string, fileToUpload: Ref<FileToUpload>, token: string, mockUpload: boolean) => {
  const uploadFn = mockUpload ? mockUploadfunction : uploadFile;
  // if we are already uploading or the upload is successful, we don't want to start another upload
  if (fileToUpload.value.state === UploadStates.uploading || fileToUpload.value.state === UploadStates.success) {
    console.log('already uploading or success', fileToUpload.value.uuid, fileToUpload.value.file.name);
    return;
  }
  // if we are pending or failed, we want to retry the upload
  fileToUpload.value.state = UploadStates.uploading;
  fileToUpload.value.progress = 0;
  const updateProgress = (progressUpdate: number) => fileToUpload.value.progress = progressUpdate;

  try {
    const reponse = await uploadFn(urlapiEndpoint, fileToUpload.value.file, [{ name: 'secret', value: token }], updateProgress)
    const result = JSON.parse(reponse.body);
    if (result.status !== 'ok') {
      fileToUpload.value.state = 'failed';
      throw new Error('Upload failed');
    }
    fileToUpload.value.state = 'success';
  } catch (error) {
    console.log('upload failed', fileToUpload.value.uuid, fileToUpload.value.file.name, error);
    fileToUpload.value.state = 'failed';
    return false
  }
  return true;
}

const fileTypes = [
  {
 filename: ".pdf", mime: "application/pdf", title: "PDF" 
},
  {
 filename: ".zip", mime: "application/zip", title: "ZIP" 
},
  {
 filename: ".xml", mime: "application/xml", title: "XML" 
},
  {
 filename: ".txt", mime: "text/plain", title: "TXT" 
},
  {
 filename: ".csv", mime: "text/csv", title: "CSV" 
},
  {
 filename: ".doc,.docx", mime: "application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document", title: "MS Word" 
},
  {
 filename: ".xls,.xlsx", mime: "application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", title: "MS Excel" 
},
  {
 filename: ".ppt,.pptx", mime: "application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation", title: "MS PowerPoint" 
},
  {
 filename: ".doc,.docx", mime: "application/msword / application/vnd.openxmlformats-officedocument.wordprocessingml.document", title: "MS Word" 
},
  {
 filename: ".xls,.xlsx", mime: "application/vnd.ms-excel / application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", title: "MS Excel" 
},
  {
 filename: ".ppt,.pptx", mime: "application/vnd.ms-powerpoint / application/vnd.openxmlformats-officedocument.presentationml.presentation", title: "MS PowerPoint" 
},
  {
 filename: ".png", mime: "image/png", title: "PNG" 
},
  {
 filename: ".jpg", mime: "image/jpeg", title: "JPEG" 
},
  {
 filename: ".gif", mime: "image/gif", title: "GIF" 
}
];


export function mapMimeToFilename(mimeType: string) {
  const foundType = fileTypes.find(item => {
    // Split the mime string by comma and check if the input mimeType is included
    const allowedMimes = item.mime.split(',').map(m => m.trim())
    return allowedMimes.includes(mimeType);
  });
  // Return the filename if found, otherwise return undefined
  return foundType;
}
